/**
 * 🧩 Curated synergy table (v2.38): moved here from the docs exporter so
 * the GAME can read it too — training-ground offers and reward/relic
 * pools weight their rolls with it (per Javi: bias toward synergies,
 * lightly). The docs export (`npm run export:data`) imports this same
 * table, so the spreadsheets and the live bias can never drift.
 *
 * ++ strong combo · + good combo · ~ situational · X anti-synergy
 */
export type SynergyStrength = '++' | '+' | '~' | 'X'

export const SYNERGY_COMBOS: [skill: string, relic: string, s: SynergyStrength, why: string][] = [
  ['overclock', 'relic-elemental-spark', '++', 'Apex Crest keeps buffing the ever-growing top element'],
  ['overclock', 'relic-specialist-badge', '+', 'solo snowball: +15% keeps the win streak feeding the stat'],
  ['overclock', 'relic-perfect-plan', '+', '100% loops stack wins AND +5 XP'],
  ['trailblazer', 'relic-short-fuse', '++', 'instant deploy laughs at tighter clocks, pockets the +3 XP'],
  ['trailblazer', 'relic-warning-bell', '~', 'the trip out is free either way; the bell still speeds the walk home'],
  ['trailblazer', 'relic-fast-deployment', 'X', 'redundant on its own teams — both erase the trip out'],
  ['trailblazer', 'relic-calm-hourglass', '~', 'less needed, but covers the teams it is not on'],
  ['aegis', 'relic-risk-taker', '+', 'chip-free boss wins at spicy chances, +2 XP on top'],
  ['aegis', 'relic-safety-margin', '~', 'belt and braces: wins chip-free, losses hit softer'],
  ['mentor', 'relic-study-group', '++', 'group XP piles onto the +3 teaching bonus'],
  ['mentor', 'relic-purpose-charm', '++', 'bigger classes: +1 slot means one more student'],
  ['mentor', 'relic-training-manual', '+', 'every success stacks another +1 on the class'],
  ['colossus', 'relic-elemental-spark', '++', 'Apex Crest feeds the doubled element (per its combo note)'],
  ['colossus', 'relic-specialist-badge', '++', 'the wall solos at +15%'],
  ['star-pupil', 'relic-training-manual', '++', 'doubles to +2 per success'],
  ['star-pupil', 'relic-shared-lessons', '++', 'doubles to +6 per failure — it cannot lose'],
  ['star-pupil', 'relic-perfect-plan', '++', 'doubles to +10 on 100% dispatches'],
  ['star-pupil', 'relic-study-group', '+', 'doubles the group bonus'],
  ['star-pupil', 'relic-solo-practice', '+', 'doubles the solo bonus'],
  ['star-pupil', 'relic-short-fuse', '+', 'doubles the +3 XP rider'],
  ['golden-touch', 'relic-midas-charm', '++', 'the +5 gold rides gold missions Midas already doubles'],
  ['golden-touch', 'relic-haggler-charm', '+', 'earn double-ish, spend at half price'],
  ['momentum', 'relic-balanced-training', '++', 'the +1-to-lowest props up exactly what the stacks drain'],
  ['momentum', 'relic-quick-whistle', '+', 'stacked ⚔️ plus shorter timers: fights evaporate'],
  ['impulse', 'relic-duo-core', '++', 'a two-element creature IS its own highest and lowest — every stack lands +1/+1 on the pair'],
  ['impulse', 'relic-swift-boots', '+', 'offsets its −1 speed'],
  ['impulse', 'relic-warning-bell', '+', 'covers the slow anchor on urgent dispatches'],
  ['second-calling', 'relic-duo-core', '+', 'two-element statlines make the second passive real'],
  ['tough-learner', 'relic-risk-taker', '++', 'both PAY on risky dispatches — win +2, lose +5'],
  ['tough-learner', 'relic-shared-lessons', '++', 'a failed arm cashes +8 XP total'],
  ['tough-learner', 'relic-team-journal', '+', 'the fail also arms the +10% comeback'],
  ['pain-into-power', 'relic-close-enough', '++', 'farm +1s at 85%+ fails with zero injuries'],
  ['pain-into-power', 'relic-shared-lessons', '++', 'every farmed fail also pays +3 XP'],
  ['pain-into-power', 'relic-safety-net', '+', 'the first farmed fail is free'],
  ['pain-into-power', 'relic-medic-band', '+', 'first farmed fail injures nobody'],
  ['rallying-cry', 'relic-overcharge', '++', 'endless DOUBLED surges, team-wide resets (per its note)'],
  ['blood-pact', 'relic-lucky-compass', '+', 'pay 2 HP to cross 65%, then +10% more on top'],
  ['blood-pact', 'relic-clean-coverage', '+', 'stacks the +10s toward guaranteed'],
  ['grudge', 'relic-team-journal', '++', 'one failure arms BOTH: +30% on the revenge dispatch'],
  ['grudge', 'relic-shared-lessons', '+', 'the triggering failure at least pays XP'],
  ['deep-roots', 'relic-calm-hourglass', '+', 'longer clocks = longer profitable naps'],
  ['deep-roots', 'relic-steady-clock', '+', 'every success extends nap time'],
  ['pacesetter', 'relic-quick-whistle', '++', '−2s on already-melted fight timers'],
  ['pacesetter', 'relic-focus-bell', '++', 'Opening Flight stacks 35% onto the ⚔️ pile early'],
  ['apotheosis', 'relic-training-manual', '+', 'anything that races to level 11'],
  ['apotheosis', 'relic-shared-lessons', '+', 'XP from failures still counts toward 11'],
  ['apotheosis', 'relic-perfect-plan', '+', '+5 per perfect dispatch shortens the chase'],
  ['equilibrium', 'relic-clean-coverage', '++', 'the even spread partly covers EVERYTHING: +10% nearly always on'],
  ['equilibrium', 'relic-balanced-training', '++', 'even spread means every element is above 0 and buffable'],
  ['equilibrium', 'relic-missing-piece', '+', 'flat spreads plus the virtual point plug any gap'],
]

export const SAME_TYPE_COMBOS: [a: string, b: string, s: SynergyStrength, why: string][] = [
  ['relic-lucky-compass', 'relic-clean-coverage', '+', 'the two +10s stack most dispatches toward guaranteed'],
  ['relic-midas-charm', 'relic-haggler-charm', '+', 'earn double, spend at half — the gold economy inverts'],
  ['relic-training-manual', 'relic-study-group', '+', 'every group success pays both bonuses'],
  ['relic-short-fuse', 'relic-steady-clock', '+', 'each success claws back the seconds Short Fuse sold'],
  ['relic-short-fuse', 'relic-calm-hourglass', '~', 'offsetting timers — the +10s eats the −2s early on'],
  ['relic-purpose-charm', 'relic-study-group', '+', 'the extra slot turns pairs into 3+ payouts'],
  ['relic-safety-net', 'relic-medic-band', '+', 'the first disaster each encounter is completely free'],
  ['overclock', 'rallying-cry', '+', 'endless surges keep the win streak (and the +1s) rolling'],
  ['momentum', 'impulse', '++', 'on one creature: Impulse’s +lowest exactly refills what Momentum drains, double ⚔️'],
  ['mentor', 'star-pupil', '++', 'a Star Pupil teammate banks +6 from every taught success'],
  ['trailblazer', 'pacesetter', '++', 'instant out, then a quickened convoy home — the full round trip melts'],
  ['tough-learner', 'pain-into-power', '++', 'one armed failure cashes +5 XP AND a permanent +1'],
  ['grudge', 'tough-learner', '+', 'the failure pays XP, then the revenge dispatch gets +20%'],
  ['apotheosis', 'star-pupil', '++', 'double XP is the fastest road to level 11'],
  ['equilibrium', 'colossus', 'X', 'the even spread has no allocated peak for Colossus to double'],
]

const POSITIVE = new Set<SynergyStrength>(['++', '+'])

/** id → its positive partners / redundant partners, precomputed. */
const partners = new Map<string, { good: string[]; redundant: string[] }>()
for (const [a, b, s] of [...SYNERGY_COMBOS, ...SAME_TYPE_COMBOS]) {
  for (const [x, y] of [[a, b], [b, a]] as const) {
    const entry = partners.get(x) ?? { good: [], redundant: [] }
    if (POSITIVE.has(s)) entry.good.push(y)
    else if (s === 'X') entry.redundant.push(y)
    partners.set(x, entry)
  }
}

/**
 * Offer-weight multiplier (v2.38, per Javi: "slightly less than 2x,
 * don't want it too crazy"): 1.6× when the item pairs positively with
 * anything the run owns; 0.75× when it's REDUNDANT with something
 * owned (never offer Fast Deployment to a Trailblazer roster at full
 * odds). Neutral otherwise.
 */
export function synergyWeightMult(id: string, owned: ReadonlySet<string>): number {
  const entry = partners.get(id)
  if (!entry) return 1
  if (entry.good.some(p => owned.has(p))) return 1.6
  if (entry.redundant.some(p => owned.has(p))) return 0.75
  return 1
}

/** Everything a run "owns" for synergy purposes: relics + every trained
 * skill on the roster. */
export function ownedSynergyIds(run: { relics: string[]; roster: { learnedSkills?: string[] }[] }): Set<string> {
  const owned = new Set(run.relics)
  for (const c of run.roster) for (const id of c.learnedSkills ?? []) owned.add(id)
  return owned
}
